import React, { FC, useMemo } from 'react';
import useTranslation from '../../../hooks/useTranslation';

import FILE_TYPES from '../../../constants/FILE_TYPES';
import formatBytes from '../../../utils/format/formatBytes';
import { StyledHint } from './Upload.styles';
import getLanguageCode from '../../../utils/language/getLanguageCode';

export interface UploadHintProps {
  maxSize: number;
  accept: string;
}

const typeMap = Object.keys(FILE_TYPES).reduce((result: Record<string, any>, typeName: string) => {
  FILE_TYPES[typeName as keyof typeof FILE_TYPES].split(',').forEach((type: string) => {
    result[type.trim()] = typeName;
  });
  return result;
}, {});

const typeNamesMap: Record<string, any> = {
  WORD: 'Microsoft Word (.doc)',
  EXCEL: 'Microsoft Excel (.xls)',
  HTML: 'html',
  TEXT: 'text',
};

const UploadHint: FC<UploadHintProps> = ({ accept, maxSize }) => {
  const { t, language } = useTranslation('Core.Upload');

  const resolvedType = useMemo(() => {
    if (!accept) {
      return '';
    }

    const types = accept.toLowerCase().split(',').map((type) => {
      const trimmedType = type.trim();
      const typeName = typeMap[trimmedType];

      return typeNamesMap[typeName] || trimmedType.replace(/[./*]/g, '');
    });

    const dedupedTypes = Array.from(new Set(types));

    if (dedupedTypes.length === 1) {
      return dedupedTypes[0];
    }

    const and = getLanguageCode(language) === 'fr' ? 'et' : 'and';

    return `${dedupedTypes.slice(0, -1).join(', ')} ${and} ${dedupedTypes[dedupedTypes.length - 1]}`;
  }, [accept, language]);

  return (
    <StyledHint>
      {t('typeAndSizeHint', { type: resolvedType, size: formatBytes(maxSize, { locale: getLanguageCode(language) }) })}
    </StyledHint>
  );
};

export default UploadHint;
